'use client';

import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Server, Cloud, HardDrive, Users, FolderSync, Brain, BarChart3, BellRing, ClipboardList, Bot, ChevronDown, CheckCircle2, DollarSign, Mail, MessageSquare, XCircle} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';

/* ══════════════════════════════════════════════
   SnapCodex 상용 서비스 안내 & 요금제
   Tailwind CSS + framer-motion (동일 디자인 시스템)
   ══════════════════════════════════════════════ */

type Tab = 'pipeline' | 'pricing' | 'roi' | 'hardware';

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const PIPELINE_STEPS = [
    { icon: FolderSync, label: 'NAS 동기화', sub: 'SnapCodex NAS 신설 → 기존 NAS 실시간 동기화', duration: '1~2주', color: 'blue' },
    { icon: ClipboardList, label: '데이터 정제', sub: '프로젝트 ID별 분류 → 학습 데이터 생성', duration: '2~4주', color: 'indigo' },
    { icon: Brain, label: 'DSLM 생성', sub: '회사 전용 AI 모델 파인튜닝', duration: '2~4주', color: 'purple' },
    { icon: BarChart3, label: '공사비 추적', sub: '온톨로지 기반 NAS 변경 diff → 공사비 자동 재산출', duration: '상시', color: 'amber' },
    { icon: BellRing, label: '예산 알림', sub: '사업예산 초과 시 의사결정 요청 (텔레그램/카카오/이메일)', duration: '상시', color: 'red' },
    { icon: Users, label: '관리자 리포트', sub: 'NAS 업데이트 통계 · 주요 진행상황 에이전트 자동 리포트', duration: '상시', color: 'emerald' },
    { icon: Bot, label: 'AI Tools', sub: '직원별 AI 도구 11종 (RAG챗/견적/도면분석/공정관리)', duration: '상시', color: 'blue' },
];

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; glow: string }> = {
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
    indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/20', text: 'text-indigo-400', glow: 'shadow-indigo-500/20' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', glow: 'shadow-purple-500/20' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', glow: 'shadow-red-500/20' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
};

export default function PricingPage() {
    const [data, setData] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<Tab>('pipeline');
    const [selectedTier, setSelectedTier] = useState('pro');
    const [userCount, setUserCount] = useState(10);
    const [projectCount, setProjectCount] = useState(5);
    const [activeStep, setActiveStep] = useState(0);

    const { theme } = useTheme();
    const isDark = theme === 'dark';

    useEffect(() => {
        fetch('/api/subscription/plans').then(r => r.json()).then(setData).catch(() => {});
    }, []);

    useEffect(() => {
        const t = setInterval(() => setActiveStep(s => (s + 1) % 7), 3000);
        return () => clearInterval(t);
    }, []);

    const formatWon = (n: number) => n >= 10000 ? `${(n / 10000).toFixed(0)}억원` : `${n.toLocaleString()}만원`;
    const calcTotal = (tier: any) => {
        if (!tier) return { setup: 0, monthly: 0, annual: 0, total1y: 0 };
        const p = tier.pricing;
        const setup = p.setup.amount + (p.dataService.amount * projectCount);
        const monthly = p.subscription.amount + (p.perUser.amount * userCount);
        return { setup, monthly, annual: monthly * 12, total1y: setup + monthly * 12 };
    };

    const TABS: { key: Tab; label: string; icon: any }[] = [
        { key: 'pipeline', label: '서비스 파이프라인', icon: FolderSync },
        { key: 'pricing', label: '요금제', icon: BarChart3 },
        { key: 'roi', label: 'ROI 시뮬레이션', icon: BarChart3 },
        { key: 'hardware', label: '하드웨어', icon: Server },
    ];

    return (
        <main className={`min-h-screen pt-16 transition-colors duration-300 ${isDark ? 'bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50 text-gray-900'}`}>
            {/* ── Hero ── */}
            <section className="relative overflow-hidden py-20 md:py-28">
                <div className="absolute inset-0 pointer-events-none">
                    <div className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(ellipse_at_center,_rgba(99,102,241,0.08)_0%,transparent_70%)]' : 'bg-[radial-gradient(ellipse_at_center,_rgba(99,102,241,0.04)_0%,transparent_70%)]'}`} />
                </div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <motion.div {...fadeUp} className="text-center mb-16">
                        <div className={`inline-flex items-center gap-2 rounded-full px-5 py-2 mb-8 backdrop-blur-md border shadow-lg ${isDark ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500" />
                            </span>
                            <span className={`text-sm font-extrabold tracking-widest uppercase ${isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>
                                SNAPCODEX FOR BUSINESS
                            </span>
                        </div>
                        <h1 className={`text-3xl md:text-5xl lg:text-[3.5rem] font-black mb-6 leading-snug tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            건축설계 회사의 NAS를<br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
                                AI가 이해하는 지식 자산
                            </span>으로
                        </h1>
                        <p className={`text-lg md:text-xl max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            기존 NAS의 도면 · 내역서 · 시방서를 자동 정제하여 관계형 데이터로 변환하고,<br className="hidden md:block" />
                            공사비 실시간 추적 · 예산 알림 · AI 에이전트 11종을 직원들이 바로 사용합니다.
                        </p>
                    </motion.div>

                    {/* ── 3D Animated Pipeline Ring ── */}
                    <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="relative mx-auto" style={{ maxWidth: 900, height: 280 }}>
                        {/* Orbit ellipses */}
                        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[180px] rounded-full border ${isDark ? 'border-white/[0.04]' : 'border-gray-200/50'}`} />
                        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[130px] rounded-full border border-dashed ${isDark ? 'border-white/[0.03]' : 'border-gray-200/30'}`} />

                        {/* Steps around ellipse */}
                        {PIPELINE_STEPS.map((step, i) => {
                            const angle = (i / 7) * 360 - 90;
                            const rad = (angle * Math.PI) / 180;
                            const rx = 360, ry = 80;
                            const x = 50 + Math.cos(rad) * rx / 9;
                            const y = 50 + Math.sin(rad) * ry / 1.6;
                            const isActive = i === activeStep;
                            const c = COLOR_MAP[step.color] || COLOR_MAP.blue;
                            const Icon = step.icon;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: isActive ? 1.15 : 1 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="absolute cursor-pointer"
                                    style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)' }}
                                    onClick={() => setActiveStep(i)}
                                >
                                    <div className={`flex flex-col items-center transition-all duration-500 ${isActive ? 'scale-110' : 'opacity-60 hover:opacity-90'}`}>
                                        <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full ${c.bg} border ${c.border} flex items-center justify-center transition-all duration-500 ${isActive ? `shadow-lg ${c.glow} ring-2 ring-offset-2 ${isDark ? 'ring-offset-gray-950' : 'ring-offset-white'} ring-current ${c.text}` : ''}`}>
                                            <Icon className={`w-6 h-6 md:w-7 md:h-7 ${c.text}`} />
                                        </div>
                                        <span className={`text-[10px] md:text-xs font-bold mt-2 whitespace-nowrap ${isActive ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                                            STEP {i + 1}
                                        </span>
                                        <span className={`text-[10px] md:text-xs font-medium whitespace-nowrap ${isActive ? c.text : (isDark ? 'text-gray-600' : 'text-gray-400')}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                </motion.div>
                            );
                        })}

                        {/* Center Hub */}
                        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 md:w-32 md:h-32 rounded-full flex flex-col items-center justify-center z-10 border-2 backdrop-blur-xl ${isDark ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
                            <Bot className={`w-8 h-8 mb-1 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                            <span className={`text-xs font-bold ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>SnapCodex</span>
                            <span className={`text-[9px] ${isDark ? 'text-indigo-500' : 'text-indigo-400'}`}>AI Engine</span>
                        </div>
                    </motion.div>

                    {/* Active Step detail card */}
                    <motion.div
                        key={activeStep}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`max-w-2xl mx-auto mt-8 p-5 rounded-2xl border backdrop-blur-md ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            {(() => { const Icon = PIPELINE_STEPS[activeStep].icon; const c = COLOR_MAP[PIPELINE_STEPS[activeStep].color]; return <div className={`w-10 h-10 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}><Icon className={`w-5 h-5 ${c.text}`} /></div>; })()}
                            <div>
                                <div className={`text-xs font-bold ${COLOR_MAP[PIPELINE_STEPS[activeStep].color].text}`}>STEP {activeStep + 1} · {PIPELINE_STEPS[activeStep].duration}</div>
                                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{PIPELINE_STEPS[activeStep].label}</h3>
                            </div>
                        </div>
                        <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{PIPELINE_STEPS[activeStep].sub}</p>
                    </motion.div>

                    {/* Linear flow at bottom */}
                    <div className="flex items-center justify-center gap-1 mt-8 flex-wrap">
                        {PIPELINE_STEPS.map((step, i) => {
                            const c = COLOR_MAP[step.color];
                            const Icon = step.icon;
                            return (
                                <div key={i} className="flex items-center gap-1">
                                    <button onClick={() => setActiveStep(i)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all text-xs font-medium border cursor-pointer ${i === activeStep ? `${c.bg} ${c.border} ${c.text}` : (isDark ? 'bg-white/[0.02] border-white/5 text-gray-500 hover:text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-gray-600')}`}>
                                        <Icon className="w-3.5 h-3.5" />
                                        <span className="hidden md:inline">{step.label}</span>
                                    </button>
                                    {i < PIPELINE_STEPS.length - 1 && <ArrowRight className={`w-3 h-3 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── Tabs ── */}
            <nav className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-gray-950/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
                <div className="max-w-7xl mx-auto px-6 flex gap-0">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.key} onClick={() => setActiveTab(t.key)}
                                className={`px-5 py-3.5 text-sm font-medium cursor-pointer flex items-center gap-2 border-b-2 transition-all ${activeTab === t.key ? (isDark ? 'border-indigo-500 text-white' : 'border-indigo-600 text-gray-900') : 'border-transparent ' + (isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')}`}>
                                <Icon className="w-4 h-4" /> {t.label}
                            </button>
                        );
                    })}
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-6 py-16">
                {/* ══════════ Pipeline Tab ══════════ */}
                {activeTab === 'pipeline' && data?.pipeline && (
                    <motion.div {...fadeUp}>
                        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>서비스 도입 7단계 파이프라인</h2>
                        <p className={`text-sm mb-12 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>기존 NAS → 데이터 정제 → AI 에이전트 구동까지 체계적인 도입 프로세스</p>

                        <div className="relative">
                            <div className={`absolute left-7 top-10 bottom-10 w-0.5 ${isDark ? 'bg-gradient-to-b from-indigo-500/30 via-purple-500/30 to-emerald-500/30' : 'bg-gradient-to-b from-indigo-200 via-purple-200 to-emerald-200'}`} />
                            {data.pipeline.map((step: any, i: number) => {
                                const c = COLOR_MAP[PIPELINE_STEPS[i]?.color || 'blue'];
                                const Icon = PIPELINE_STEPS[i]?.icon || FolderSync;
                                return (
                                    <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                                        className="flex gap-5 mb-6 relative">
                                        <div className={`w-14 h-14 rounded-full ${c.bg} border ${c.border} flex items-center justify-center flex-shrink-0 z-10 shadow-lg ${c.glow}`}>
                                            <Icon className={`w-6 h-6 ${c.text}`} />
                                        </div>
                                        <div className={`flex-1 p-5 rounded-2xl border transition-all ${isDark ? 'bg-white/[0.02] border-white/5 hover:border-white/10' : 'bg-white border-gray-200 shadow-sm hover:shadow-md'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    <span className={`text-xs ${c.text} mr-2`}>STEP {step.step}</span>
                                                    {step.title}
                                                </h3>
                                                <span className={`text-[10px] px-2.5 py-1 rounded-full ${c.bg} ${c.text} font-medium`}>{step.duration}</span>
                                            </div>
                                            <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{step.desc}</p>
                                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{step.detail}</p>
                                            <div className={`mt-3 text-xs flex items-center gap-1.5 ${c.text}`}>
                                                <CheckCircle className="w-3.5 h-3.5" /> {step.deliverable}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* ══════════ Pricing Tab ══════════ */}
                {activeTab === 'pricing' && data?.tiers && (
                    <motion.div {...fadeUp}>
                        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>요금제</h2>
                        <p className={`text-sm mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>회사 규모에 맞는 플랜을 선택하세요. 모든 플랜은 초기 구축비 + 월 구독료 + 계정당 비용으로 구성됩니다.</p>

                        {/* Simulator */}
                        <motion.div {...fadeUp} transition={{ delay: 0.1 }}
                            className={`p-5 rounded-2xl border mb-10 ${isDark ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50 border-indigo-200'}`}>
                            <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                                <BarChart3 className="w-4 h-4" /> 비용 시뮬레이터
                            </h3>
                            <div className="flex gap-8 flex-wrap">
                                <label className={`text-sm flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    <Users className="w-4 h-4" /> 직원 수
                                    <input type="range" min={1} max={100} value={userCount} onChange={e => setUserCount(+e.target.value)} className="w-32 accent-indigo-500" />
                                    <span className={`font-bold min-w-[3rem] ${isDark ? 'text-white' : 'text-gray-900'}`}>{userCount}명</span>
                                </label>
                                <label className={`text-sm flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    <FolderSync className="w-4 h-4" /> 프로젝트 수
                                    <input type="range" min={1} max={20} value={projectCount} onChange={e => setProjectCount(+e.target.value)} className="w-32 accent-indigo-500" />
                                    <span className={`font-bold min-w-[3rem] ${isDark ? 'text-white' : 'text-gray-900'}`}>{projectCount}건</span>
                                </label>
                            </div>
                        </motion.div>

                        {/* Tier Cards */}
                        <div className="grid md:grid-cols-3 gap-8">
                            {data.tiers.map((tier: any, idx: number) => {
                                const costs = calcTotal(tier);
                                const icons = [Cloud, Server, HardDrive];
                                const colors = ['blue', 'indigo', 'purple'];
                                const Icon = icons[idx];
                                const isPro = idx === 1;
                                return (
                                    <motion.div key={tier.id} {...fadeUp} transition={{ delay: idx * 0.1 }}
                                        onClick={() => setSelectedTier(tier.id)}
                                        className={`rounded-2xl p-7 transition-all cursor-pointer relative ${isPro ? (isDark ? 'bg-gradient-to-b from-indigo-900/30 to-gray-900/50 border-2 border-indigo-500/30' : 'bg-white border-2 border-indigo-400 shadow-lg shadow-indigo-100') : (isDark ? 'bg-gray-900/50 border border-white/10 hover:border-white/20' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md')}`}>
                                        {isPro && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold">추천</div>}
                                        <div className="flex items-center gap-3 mb-4">
                                            <Icon className={`w-6 h-6 text-${colors[idx]}-400`} />
                                            <span className={`text-sm text-${colors[idx]}-400 font-medium`}>{tier.badge}</span>
                                        </div>
                                        <h3 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tier.name}</h3>
                                        <p className={`text-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tier.subtitle}</p>

                                        <div className={`p-4 rounded-xl mb-5 space-y-2 ${isDark ? 'bg-white/[0.02] border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                                            {Object.values(tier.pricing).map((p: any, i: number) => (
                                                <div key={i} className="flex justify-between text-sm">
                                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{p.label}</span>
                                                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.amount}{p.unit}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className={`p-4 rounded-xl mb-5 ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-100'}`}>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>초기 구축 (총)</span>
                                                <span className="text-amber-400 font-bold">{formatWon(costs.setup)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>월 이용료 ({userCount}명)</span>
                                                <span className="text-emerald-400 font-bold">{formatWon(costs.monthly)}</span>
                                            </div>
                                            <div className={`flex justify-between text-sm pt-2 mt-1 border-t font-bold ${isDark ? 'border-white/5 text-indigo-400' : 'border-gray-200 text-indigo-600'}`}>
                                                <span>1년차 총비용</span>
                                                <span>{formatWon(costs.total1y)}</span>
                                            </div>
                                        </div>

                                        <ul className="space-y-2 mb-6">
                                            {tier.features.map((f: string, i: number) => (
                                                <li key={i} className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /> {f}
                                                </li>
                                            ))}
                                        </ul>
 <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}> {tier.recommended}</p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* ══════════ ROI Tab ══════════ */}
                {activeTab === 'roi' && data?.roi && (
                    <motion.div {...fadeUp}>
                        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>ROI 시뮬레이션</h2>
                        <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>SnapCodex 도입 전후 업무 효율 비교</p>

                        <div className="grid md:grid-cols-2 gap-8 mb-10">
                            <motion.div {...fadeUp} transition={{ delay: 0.1 }}
                                className={`rounded-2xl p-6 border ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50 border-red-200'}`}>
                                <h3 className="text-lg font-bold text-red-400 mb-5 flex items-center gap-2">
 <span className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-sm"><XCircle className="w-4 h-4 inline-block" /></span>도입 전
                                </h3>
                                {Object.entries(data.roi.before).map(([k, v]: [string, any]) => (
                                    <div key={k} className={`flex justify-between py-2.5 border-b text-sm ${isDark ? 'border-white/5' : 'border-red-100'}`}>
                                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{{ costEstimateTime: '견적 산출', designReviewTime: '설계 검토', costOverrunRate: '공사비 초과율', documentSearchTime: '문서 검색' }[k]}</span>
                                        <span className="text-red-400 font-bold">{v}</span>
                                    </div>
                                ))}
                            </motion.div>
                            <motion.div {...fadeUp} transition={{ delay: 0.2 }}
                                className={`rounded-2xl p-6 border ${isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-200'}`}>
                                <h3 className="text-lg font-bold text-emerald-400 mb-5 flex items-center gap-2">
 <span className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-sm"><CheckCircle2 className="w-4 h-4 inline-block" /></span>도입 후
                                </h3>
                                {Object.entries(data.roi.after).map(([k, v]: [string, any]) => (
                                    <div key={k} className={`flex justify-between py-2.5 border-b text-sm ${isDark ? 'border-white/5' : 'border-emerald-100'}`}>
                                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{{ costEstimateTime: '견적 산출', designReviewTime: '설계 검토', costOverrunRate: '공사비 초과율', documentSearchTime: '문서 검색' }[k]}</span>
                                        <span className="text-emerald-400 font-bold">{v}</span>
                                    </div>
                                ))}
                            </motion.div>
                        </div>

 <h3 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}><DollarSign className="w-4 h-4 inline-block" />연간 절감 효과</h3>
                        <div className="grid md:grid-cols-2 gap-4 mb-8">
                            {Object.entries(data.roi.annualSavings).map(([k, v]: [string, any], i: number) => (
                                <motion.div key={k} {...fadeUp} transition={{ delay: i * 0.1 }}
                                    className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className={`text-xs font-bold mb-1 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
 {[' 견적 산출 자동화', ' 설계 검토 자동화', ' 공사비 초과 방지', ' 문서 검색 효율화'][i]}
                                    </div>
                                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{v}</div>
                                </motion.div>
                            ))}
                        </div>
                        <div className={`p-5 rounded-2xl border text-center ${isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-200'}`}>
                            <span className="text-xl font-bold text-emerald-400">연간 예상 절감액: 약 8,200만원</span>
                            <span className={`text-sm ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(20명 기준 설계사무소)</span>
                        </div>
                    </motion.div>
                )}

                {/* ══════════ Hardware Tab ══════════ */}
                {activeTab === 'hardware' && data?.hardware && (
                    <motion.div {...fadeUp}>
                        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>하드웨어 패키지</h2>
                        <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>엘리트 플랜 — 온프레미스 전용 서버 구성</p>
                        <div className="grid md:grid-cols-3 gap-8">
                            {data.hardware.map((hw: any, i: number) => {
                                const icons = [Cloud, Server, HardDrive];
                                const colors = ['blue', 'indigo', 'purple'];
                                const Icon = icons[i];
                                return (
                                    <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                                        className={`rounded-2xl p-7 border ${isDark ? `bg-gradient-to-b from-${colors[i]}-900/20 to-transparent border-${colors[i]}-500/20` : `bg-white border-gray-200 shadow-sm hover:shadow-md`}`}>
                                        <Icon className={`w-8 h-8 text-${colors[i]}-400 mb-4`} />
                                        <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{hw.name}</h3>
                                        <p className={`text-sm mb-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{hw.target}</p>
                                        <div className={`text-2xl font-bold mb-5 text-${colors[i]}-400`}>{hw.price}</div>
                                        <div className={`text-sm space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            <div className="flex items-center gap-2"><Server className="w-4 h-4 text-gray-500" /> {hw.spec}</div>
                                            <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-gray-500" /> 모델: {hw.model}</div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* CTA */}
            <section className={`py-16 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <motion.div {...fadeUp}>
                        <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>도입 상담 신청</h3>
                        <p className={`text-sm mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>회사 규모와 요구사항에 맞는 맞춤 견적을 안내드립니다</p>
                        <div className="flex gap-4 justify-center flex-wrap">
                            <a href="https://t.me/desinsight_Poby_bot" target="_blank" rel="noreferrer"
                                className="px-8 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-xl text-white font-bold text-sm transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2">
 <MessageSquare className="w-4 h-4 inline-block" />텔레그램 상담 <ArrowRight className="w-4 h-4" />
                            </a>
                            <a href="mailto:contact@desinsight.com"
                                className={`px-8 py-3.5 rounded-xl font-semibold text-sm transition-all border flex items-center gap-2 ${isDark ? 'bg-white/5 hover:bg-white/10 border-white/10 text-white' : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700 shadow-sm'}`}>
 <Mail className="w-4 h-4 inline-block" />이메일 문의
                            </a>
                            <a href="/"
                                className={`px-8 py-3.5 rounded-xl text-sm transition-all border ${isDark ? 'border-white/5 text-gray-500 hover:text-gray-300' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}>
                                ← 홈으로
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            <footer className={`py-6 text-center text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                SnapCodex Business — 건축설계 AI 솔루션 © 2026 Desinsight
            </footer>
        </main>
    );
}
